import { Loader2 } from 'lucide-react';
import { formatCNPJ, formatDataHoraHistorico } from '@/lib/utils/formatters';
import { useEventosNfse, type EventoNfse } from '@/services/ServicosNfse';

interface EventosNfseSectionProps {
  chaveAcessoDoc: string | null;
  open: boolean;
}

/** Os três tipos que o serviço de captura grava em `MANFSEVE`, para quando a descrição vier vazia. */
const DESCRICAO_POR_TIPO: Record<string, string> = {
  '101101': 'Cancelamento de NFS-e',
  '203202': 'Manifestação de NFS-e - Confirmação do Tomador',
  '203206': 'Manifestação de NFS-e - Rejeição do Tomador',
};

function descreverEvento(evento: EventoNfse) {
  const descricao = evento.xDesc?.trim();
  if (descricao) return descricao;
  return DESCRICAO_POR_TIPO[evento.TpEvento?.trim() ?? ''] ?? evento.TpEvento ?? 'Evento';
}

function descreverAutor(documento: string | null) {
  const digitos = (documento ?? '').replace(/\D/g, '');
  if (digitos.length === 14) return formatCNPJ(digitos);
  return documento?.trim() || null;
}

function descreverMotivo(evento: EventoNfse) {
  const partes = [evento.cMotivo?.trim(), evento.xMotivo?.trim()].filter(Boolean);
  return partes.length > 0 ? partes.join(' - ') : null;
}

/**
 * Os eventos da nota (SPEC-011, T7 e T9): cancelamento e manifestação do tomador, os mais recentes primeiro, com o
 * motivo quando houver. Vêm da API (`EventosNFS`), que confere o vínculo do gestor.
 */
export function EventosNfseSection({ chaveAcessoDoc, open }: EventosNfseSectionProps) {
  const { data: eventos, isLoading, error } = useEventosNfse(chaveAcessoDoc, open);

  if (isLoading) {
    return (
      <div className="flex items-center py-4 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Carregando eventos da NFS-e...
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">{error.message}</p>;
  }

  if (!eventos || eventos.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Nenhum evento registrado para esta NFS-e.</p>
    );
  }

  return (
    <ul className="space-y-3" aria-label="Eventos da NFS-e">
      {eventos.map((evento, indice) => {
        const autor = descreverAutor(evento.CNPJCPFAutorEvento);
        const motivo = descreverMotivo(evento);

        return (
          <li
            key={`${evento.TpEvento}-${evento.nSeqEvento}-${evento.DataHoraEnvio}-${indice}`}
            className="rounded-md border p-3 text-sm"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium">{descreverEvento(evento)}</p>
              <p className="font-mono text-xs text-muted-foreground">{evento.TpEvento}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              {evento.DataHoraEnvio ? formatDataHoraHistorico(evento.DataHoraEnvio) : '-'}
              {autor ? ` · Autor: ${autor}` : ''}
            </p>
            {motivo && <p className="mt-1 break-words">Motivo: {motivo}</p>}
          </li>
        );
      })}
    </ul>
  );
}
